(ns admin.containers.looker-dashboard
  (:require
   [cemerick.url :as url]
   [clojure.string :as str]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]

   [kyber.components.base.text :as text]
   [kyber.components.base.icons :as icon]
   [kyber.components.base.link :as link]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.utils :as ku]
   [kyber.components.composite.loading :as loading]))

(def classes
  (styles->classes
   {:looker-nav-link
    {:font-size (gs [:font-size :title-30])
     :padding-bottom (gs [:spacing :p8])}

    :looker-nav-link-small
    {:font-size (gs [:font-size :caption-30])}

    :top-level-breadcrumbs-container
    {:display :flex
     :padding (gs [:spacing :p20])

     "& > *"
     {:margin-right (gs [:spacing :p16])}

     "& > *:last-chlid"
     {:margin-right (gs [:spacing :p0])}}

    :active-top-level-breadcrumb
    {:cursor :default
     :color (gs [:colors :gray-scale-400])
     :border-bottom (str "8px solid " (gs [:colors :gray-scale-200]))}

    :nested-breadcrumbs-container
    {:margin-top (gs [:spacing :p20])
     :margin-left (gs [:spacing :p20])
     :display :flex
     :align-items :center

     "& > *"
     {:margin-right (gs [:spacing :p16])}

     "& > *:last-chlid"
     {:margin-right (gs [:spacing :p0])}}

    :nested-breadcrumb-container
    {:display :flex
     :align-items :center}

    :alert-section
    {:display :flex
     :justify-content :space-between
     :width "100%"
     :margin-bottom (gs [:spacing :p20])}

    :alert-context-container
    {:display :flex
     :flex-direction :column
     :align-items :center
     :justify-content :center
     :padding [(gs [:spacing :p12])
               (gs [:spacing :p20])]
     :max-width "500px"
     :width "100%"}

    :alerts-container
    {:border (str "1px solid " (gs [:colors :gray-scale-100]))
     :border-radius (gs [:radius :r3])
     :padding [(gs [:spacing :p4])
               (gs [:spacing :p20])]
     :box-shadow (gs [:shadows :shadow-12])
     :width "100%"
     :max-width "800px"
     :margin-right (gs [:spacing :p20])}

    :alert-container
    {:display :flex
     :align-items :center
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p0])]

     "&:last-child"
     {:border-bottom :none}}

    :risk-pill
    {:padding [(gs [:spacing :p2])]
     :color (gs [:colors :true-white])
     :width "60px"
     :text-align :center
     :border-radius (gs [:radius :r3])}

    :risk-text
    {:margin-left (gs [:spacing :p20])
     :padding-left (gs [:spacing :p20])
     :border-left (str "2px solid " (gs [:colors :gray-scale-200]))}}))

(defn- transition-looker-dashboard!
  [message]
  (when-let [dashboard-spec (-> message :page :absoluteUrl url/url :anchor)]
    (let [[relationship route] (str/split dashboard-spec "/")
          [dashboard-key dashboard-params] (str/split route "?")]
      (re-frame/dispatch [:insights/transition-dashboard
                          {:relationship (keyword relationship)
                           :dashboard-key dashboard-key
                           :dashboard-params dashboard-params}]))))

(defn- get-active-dashboard
  [{:keys [dashboard-key child1 child2 child3]}]
  (or child3 child2 child1 dashboard-key))

(defn- get-dashboard-by-key
  [dashboards key]
  (->> dashboards
       (filter #(= (:key %) key))
       (first)))

(defn- render-alert
  [{:keys [body risk]}]
  [:div {:class (:alert-container classes)}
   [text/body-20 :p
    {:class (:risk-pill classes)
     :style {:background-color
             (case (keyword risk)
               :high (gs [:colors :error-500])
               :medium (gs [:colors :warning-500])
               :low (gs [:colors :success-500])
               (js/console.error risk "Risk level not supported"))}}
    (case (keyword risk)
      :high "High"
      :medium "Medium"
      :low "Low"
      (js/console.error risk "Risk level not supported"))]
   [:div
    {:class ["rich-text-container" (:risk-text classes)]
     :dangerouslySetInnerHTML {:__html body}}]])

(defn- render-alerts
  [alerts]
  [:div {:class (:alerts-container classes)}
   (for [{:keys [body] :as alert} alerts]
     ^{:key body} [render-alert alert])])

(defn- render-dashboard-2
  [{:keys [params]}]
  (let [loaded
        (reagent/atom false)

        {:keys [activity-uuid curriculum-uuid]}
        params

        dashboard
        (re-frame/subscribe
         [:insights/looker-dashboard
          {:activity-uuid activity-uuid
           :curriculum-uuid curriculum-uuid
           :dashboard-key (keyword (get-active-dashboard params))}])

        !ref
        (atom nil)

        iframe-height
        (reagent/atom 0)

        on-message
        (fn [e]
          (let [{:keys [domain]} @dashboard
                event-source (.-source e)
                event-origin (.-origin e)
                content-window (.-contentWindow @!ref)]
            (when (and (= event-source content-window)
                       (= event-origin (str "https://" domain)))
              (let [{message-type :type :as message}
                    (js->clj
                     (.parse js/JSON (.-data e))
                     :keywordize-keys true)]
                (case (keyword message-type)
                  :page:properties:changed
                  (reset! iframe-height (:height message))

                  :page:changed
                  (transition-looker-dashboard! message)

                  nil)))))]

    (reagent/create-class
     {:component-did-mount
      (fn []
        (re-frame/dispatch [:insights/get-looker-dashboard
                            {:activity-uuid activity-uuid
                             :dashboard-key (get-active-dashboard params)}])

        (.addEventListener js/window "message" on-message))

      :component-will-unmount
      (fn []
        (re-frame/dispatch [:insights/clear-looker-dashboard
                            {:dashboard-key (get-active-dashboard params)}])
        (.removeEventListener js/window "message" on-message))

      :reagent-render
      (fn []
        (let [{:keys [alerts url]} @dashboard]
          [:<>
           (when (false? @loaded)
             [loading/render
              {:loading-message "Please wait while we load your dashboards"}])

           (when (and (true? @loaded)
                      (some? alerts))
             [:div {:class (:alert-section classes)}
              [render-alerts alerts]
              [:div {:class (:alert-context-container classes)}
               [text/title-30 :p
                {:style {:color (gs [:colors :gray-scale-500])}}
                "Actionable Insights"]
               [text/body-20 :p
                {:style {:color (gs [:colors :gray-scale-500])}}
                (str "Ready is analyzing the mindset and readiness of your "
                     "project teams and site personnel to assess and mitigate "
                     "clinical trial risk. Please review these alerts and the "
                     "data below to take appropriate action. ")]]])

           (when (some? url)
             [:iframe {:key (get-active-dashboard params)
                       :src url
                       :ref #(reset! !ref %)
                       :on-load #(reset! loaded true)
                       :width "100%"
                       :height @iframe-height}])]))})))

(defn- render-dashboard
  [{:keys [params resource-status]}]
  (if (ku/all-resources-loaded? resource-status)
    [render-dashboard-2 {:params params
                         :key (get-active-dashboard params)}]
    [loading/render
     {:loading-message "Please wait while we load your dashboards"}]))

(defn- render-breadcrumb
  [{:keys [route dashboard has-child]}]
  (when (some? dashboard)
    [:li {:class (:nested-breadcrumb-container classes)}
     [link/link
      (cond-> {:class (:looker-nav-link-small classes)
               :style {:margin-right (gs [:spacing :p16])}
               :href (if (true? has-child) route "javascript:;")
               :on-click #(js/console.log "Scroll TOP")}
        (false? has-child)
        (update :style merge
                {:border-bottom (str "2px solid "
                                     (gs [:colors :gray-scale-200]))
                 :color (gs [:colors :gray-scale-400])}))
      (:name dashboard)]
     (when (true? has-child)
       [text/title-10 :p ">"])]))

(defn- render-breadcrumbs
  [{:keys [params]}]
  (let [{:keys [activity-uuid curriculum-uuid]} params
        activity (re-frame/subscribe [:activities/activity
                                      {:curriculum-uuid curriculum-uuid
                                       :activity-uuid activity-uuid}])]
    (fn [{:keys [params query]}]
      (let [{:keys [looker]}
            @activity

            {:keys [dashboard-key child1 child2 child3]}
            params

            parent-route
            (str "/admin/curriculum/" curriculum-uuid
                 "/activity/" activity-uuid "/dashboards")

            {top-level-dashboard-key :key}
            (get-dashboard-by-key looker dashboard-key)

            {child-1-dashboard-key :key :as child-1-dashboard}
            (get-dashboard-by-key looker child1)

            {child-2-dashboard-key :key :as child-2-dashboard}
            (get-dashboard-by-key looker child2)

            {child-3-dashboard-key :key :as child-3-dashboard}
            (get-dashboard-by-key looker child3)

            top-level-dashboards
            (->> looker
                 (filter :top-level)
                 (mapv (fn [{tab-name :name
                             tab-key :key}]
                         {:name tab-name
                          :key tab-key})))]
        [:ul {:class (:top-level-breadcrumbs-container classes)}
         (for [dashboard top-level-dashboards]
           ^{:key (:name dashboard)}
           [:li
            [link/link
             (cond-> {:class (:looker-nav-link classes)
                      :href (str parent-route "/" (:key dashboard))}
               (= top-level-dashboard-key (:key dashboard))
               (update :class #(str % " "
                                    (:active-top-level-breadcrumb classes))))
             (:name dashboard)]

            (when (= top-level-dashboard-key (:key dashboard))
              [:ul {:class (:nested-breadcrumbs-container classes)}
               (when (some? child-1-dashboard)
                 [icon/icon-sm :level-up-alt])

               [render-breadcrumb
                {:dashboard child-1-dashboard
                 :has-child (some? child-2-dashboard)
                 :route (str parent-route
                             "/" top-level-dashboard-key
                             "/" child-1-dashboard-key
                             "?" (url/map->query query))}]

               [render-breadcrumb
                {:dashboard child-2-dashboard
                 :has-child (some? child-3-dashboard)
                 :route (str parent-route
                             "/" top-level-dashboard-key
                             "/" child-1-dashboard-key
                             "/" child-2-dashboard-key
                             "?" (url/map->query query))}]

               [render-breadcrumb
                {:dashboard child-3-dashboard
                 :has-child false
                 :route (str parent-route
                             "/" top-level-dashboard-key
                             "/" child-1-dashboard-key
                             "/" child-2-dashboard-key
                             "/" child-3-dashboard-key
                             "?" (url/map->query query))}]])])]))))

(defn render
  [router-ctx]
  [:<>
   [render-breadcrumbs router-ctx]
   [render-dashboard router-ctx]])
